<script setup lang="ts">
import { ChevronDown, Plus } from 'lucide-vue-next'
import { computed } from 'vue'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface Lender {
  id: number
  name: string
  adapter: string | null
  adapterSupport: { create: boolean; submit_application: boolean; async: boolean }
}

const props = withDefaults(
  defineProps<{
    lenders: Lender[]
    pending?: boolean
    label?: string
    variant?: 'default' | 'outline'
    align?: 'start' | 'center' | 'end'
  }>(),
  {
    pending: false,
    label: 'Request Quote',
    variant: 'default',
    align: 'end',
  },
)

const emit = defineEmits<{ select: [lender: Lender] }>()

const apiLenders = computed(() => props.lenders.filter((l) => l.adapterSupport.create))
const manualLenders = computed(() => props.lenders.filter((l) => !l.adapterSupport.create))
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button :variant="variant" size="sm" :disabled="pending">
        <Plus class="size-4" />
        {{ label }}
        <ChevronDown class="size-3" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent :align="align" class="w-56">
      <template v-if="apiLenders.length > 0">
        <DropdownMenuLabel class="text-muted-foreground text-xs font-normal">API Lenders</DropdownMenuLabel>
        <DropdownMenuGroup>
          <DropdownMenuItem
            v-for="lender in apiLenders"
            :key="lender.id"
            :disabled="pending"
            @click="emit('select', lender)"
          >
            {{ lender.name }}
            <span class="text-muted-foreground ml-auto pl-2 text-xs">API</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </template>

      <DropdownMenuSeparator v-if="apiLenders.length > 0 && manualLenders.length > 0" />

      <template v-if="manualLenders.length > 0">
        <DropdownMenuLabel class="text-muted-foreground text-xs font-normal">Manual Lenders</DropdownMenuLabel>
        <DropdownMenuGroup>
          <DropdownMenuItem
            v-for="lender in manualLenders"
            :key="lender.id"
            :disabled="pending"
            @click="emit('select', lender)"
          >
            {{ lender.name }}
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </template>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
